import React, { useMemo, useEffect, useState, useRef } from "react";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { MockMap } from "./MockMap";
import { MapPin, Navigation, Navigation2, Car } from "lucide-react";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
// Real Google Map ID (auto-generated in Cloud Console). A VECTOR map id is
// required for camera heading/tilt rotation. Falls back to the legacy literal
// so existing behaviour is unchanged until a real id is provided via env.
const MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || "taxi_dispatch_map";

/**
 * SmartMap - real Google Maps when REACT_APP_GOOGLE_MAPS_API_KEY is set, else MockMap.
 * If Google Maps fails (e.g. RefererNotAllowedMapError), falls back to MockMap.
 *
 * Optional `onStatusChange(status)` callback for ops dashboards:
 *   "initialising" → script loading / auth pending
 *   "loaded"       → tiles rendered, map fully usable
 *   "fallback"     → no API key, using the mock view (still functional)
 *   "failed"       → auth or runtime crash, MockMap fallback active
 */
export const SmartMap = (props) => {
  const { onStatusChange } = props;
  const [mapsFailed, setMapsFailed] = useState(false);
  const [failureReason, setFailureReason] = useState(null);

  const hasKey = MAPS_KEY && MAPS_KEY.trim() !== "" && MAPS_KEY !== "YOUR_GOOGLE_MAPS_API_KEY";

  useEffect(() => {
    if (!hasKey) {
      // eslint-disable-next-line no-console
      console.debug("[SmartMap] Using MockMap (no REACT_APP_GOOGLE_MAPS_API_KEY)");
      onStatusChange?.("fallback");
      return;
    }
    // eslint-disable-next-line no-console
    console.debug("[SmartMap] Initialising Google Maps");
    onStatusChange?.("initialising");
    const origAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      // eslint-disable-next-line no-console
      console.warn("[SmartMap] Google Maps auth failed — referrer/key not allowed");
      setFailureReason("Google Maps key rejected (check referrer restrictions).");
      setMapsFailed(true);
      onStatusChange?.("failed");
    };
    return () => { window.gm_authFailure = origAuthFailure; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasKey]);

  if (!hasKey || mapsFailed) {
    return (
      <>
        <MockMap {...props} />
        {failureReason && <MapFailureBanner reason={failureReason} />}
      </>
    );
  }

  return (
    <ErrorBoundary
      fallback={<MapFailureFallback {...props} />}
      onError={() => onStatusChange?.("failed")}
    >
      <RealGoogleMap {...props} onLoaded={() => onStatusChange?.("loaded")} />
    </ErrorBoundary>
  );
};

/** Tiny banner shown over the MockMap when we fall back due to a real auth failure. */
const MapFailureBanner = ({ reason }) => (
  <div
    className="absolute top-3 right-3 z-20 bg-[#FEF2F2] border-2 border-[#EF4444] px-3 py-2 text-xs font-bold text-[#991B1B] shadow-lg max-w-xs"
    data-testid="map-failure-banner"
  >
    Map degraded · {reason}
  </div>
);

/** Visible fallback when the real Google Map throws after mount.
 * We render the mock map so admins still see fleet pins + a clear message. */
const MapFailureFallback = (props) => (
  <>
    <MockMap {...props} />
    <MapFailureBanner reason="Map failed to load — using fallback view. Check console for details." />
  </>
);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[SmartMap] Map crashed inside ErrorBoundary:", error?.message, info?.componentStack);
    this.props.onError?.(error);
  }
  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

const RealGoogleMap = ({ markers = [], routes = null, className = "", showRoute = true, height = "100%", onMapClick, onMarkerClick, onLoaded, follow = false, followZoom = 16, headUp = false, headingDeg = null, onRenderingType, focusTarget = null, onUserPan, disableAutoFit = false }) => {
  const center = useMemo(() => {
    const valid = markers.filter((m) => m.lat != null && m.lng != null);
    if (valid.length === 0) return { lat: 51.2667, lng: -1.0876 };
    const lat = valid.reduce((s, m) => s + m.lat, 0) / valid.length;
    const lng = valid.reduce((s, m) => s + m.lng, 0) / valid.length;
    return { lat, lng };
  }, [markers]);

  // Navigation camera is driven imperatively by <NavCamera/> (moveCamera) to
  // avoid the controlled-vs-imperative heading conflict. We request VECTOR
  // rendering in follow mode because camera heading/tilt only apply on a vector
  // map (raster ignores them — the marker still rotates as a fallback).
  return (
    <div className={`relative w-full ${className}`} style={{ height }} data-testid="real-google-map">
      <APIProvider apiKey={MAPS_KEY}>
        <Map
          style={{ width: "100%", height: "100%" }}
          defaultCenter={center}
          defaultZoom={13}
          {...(follow ? { renderingType: "VECTOR" } : {})}
          mapId={MAP_ID}
          gestureHandling="greedy"
          disableDefaultUI={true}
          zoomControl={true}
          streetViewControl={false}
          fullscreenControl={false}
          onTilesLoaded={onLoaded}
          onClick={onMapClick ? (e) => {
            if (e.detail?.latLng) {
              onMapClick(e.detail.latLng.lat, e.detail.latLng.lng);
            }
          } : undefined}
        >
          {markers.map((m) =>
            m.lat == null || m.lng == null ? null : (
              <AdvancedMarker
                key={m.id}
                position={{ lat: m.lat, lng: m.lng }}
                clickable={!!onMarkerClick}
                onClick={onMarkerClick ? () => onMarkerClick(m) : undefined}
              >
                <PinUI marker={m} />
              </AdvancedMarker>
            )
          )}
          {routes && routes.length > 0
            ? <OverlayRoutes routes={routes} />
            : showRoute && <RoutePolyline markers={markers} />}
          <MapAutoFit markers={markers} disabled={follow || disableAutoFit} />
          <FocusOnMe markers={markers} />
          {focusTarget && <FocusTarget target={focusTarget} />}
          {onUserPan && <UserPanWatch onUserPan={onUserPan} />}
          {follow && <NavCamera markers={markers} zoom={followZoom} headUp={headUp} headingDeg={headingDeg} />}
          {onRenderingType && <RenderingTypeProbe onType={onRenderingType} />}
        </Map>
      </APIProvider>
    </div>
  );
};

/** Renders any number of explicit polylines on the map. Used by Admin's
 * route-preview overlay. Cleans up the underlying google.maps.Polyline
 * instances on unmount or when the `routes` payload changes. */
const OverlayRoutes = ({ routes }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google) return;
    const lines = routes.map((r) => {
      if (!r.points || r.points.length < 2) return null;
      const opts = {
        path: r.points,
        geodesic: true,
        strokeColor: r.color || "#0A0A0A",
        strokeOpacity: r.style === "dashed" ? 0 : (r.opacity ?? 0.9),
        strokeWeight: r.weight || 4,
      };
      if (r.style === "dashed") {
        opts.icons = [{
          icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 3, strokeColor: r.color || "#0A0A0A" },
          offset: "0",
          repeat: "12px",
        }];
      }
      const line = new window.google.maps.Polyline(opts);
      line.setMap(map);
      return line;
    }).filter(Boolean);
    return () => lines.forEach((l) => l.setMap(null));
  }, [map, routes]);
  return null;
};

const PinUI = ({ marker }) => {
  const { type, label } = marker;
  if (type === "me") {
    // Google-style "blue dot" for the rider's current location.
    return (
      <div className="relative flex items-center justify-center" data-testid="my-location-marker">
        <div className="absolute w-7 h-7 rounded-full bg-[#4285F4]/25 animate-ping" />
        <div className="w-4 h-4 rounded-full bg-[#4285F4] border-2 border-white shadow-md" />
      </div>
    );
  }
  // Directional vehicle marker for the in-app navigator: a chevron that rotates
  // to the driver's heading so it points along the direction of travel. We use
  // Navigation2 (an arrow that points STRAIGHT UP / north at 0°), NOT Navigation
  // (which points north-EAST and made the marker look ~45° off). Rotating an
  // up-pointing arrow by the compass heading aims it true to the travel course.
  if (marker.directional && Number.isFinite(marker.heading)) {
    return (
      <div className="flex flex-col items-center" data-testid="driver-heading-marker">
        <div
          className="w-11 h-11 rounded-full bg-[#002FA7] border-[3px] border-white flex items-center justify-center shadow-xl"
          style={{ transform: `rotate(${marker.heading}deg)`, transition: "transform 0.4s ease-out" }}
        >
          <Navigation2 className="w-6 h-6 text-white fill-white" strokeWidth={2} />
        </div>
        {label && (
          <div className="mt-1 bg-black text-white px-2 py-0.5 text-[10px] tracking-widest font-bold whitespace-nowrap">
            {label}
          </div>
        )}
      </div>
    );
  }
  const bg =
    marker.color ||
    (type === "pickup" ? "#10B981" : type === "dropoff" ? "#EF4444" : type === "driver" ? "#FFC107" : "#002FA7");
  const Icon = type === "driver" ? Car : type === "dropoff" ? Navigation : MapPin;
  // Honour an explicit `pulse` flag if provided (e.g. offline drivers shouldn't pulse).
  // Fallback: drivers pulse by default unless `pulse === false`.
  const shouldPulse = marker.pulse !== undefined ? marker.pulse : type === "driver";
  return (
    <div className="flex flex-col items-center">
      <div
        className={`w-9 h-9 rounded-full border-2 border-black flex items-center justify-center shadow-lg ${
          shouldPulse ? "pulse-active" : ""
        }`}
        style={{ backgroundColor: bg }}
      >
        <Icon className="w-4 h-4 text-black" strokeWidth={2.5} />
      </div>
      {label && (
        <div className="mt-1 bg-black text-white px-2 py-0.5 text-[10px] tracking-widest font-bold whitespace-nowrap">
          {label}
        </div>
      )}
    </div>
  );
};

const RoutePolyline = ({ markers }) => {
  const map = useMap();
  const pickup = markers.find((m) => m.type === "pickup");
  const dropoff = markers.find((m) => m.type === "dropoff");

  useEffect(() => {
    if (!map || !pickup || !dropoff || !window.google) return;
    const line = new window.google.maps.Polyline({
      path: [
        { lat: pickup.lat, lng: pickup.lng },
        { lat: dropoff.lat, lng: dropoff.lng },
      ],
      geodesic: true,
      strokeColor: "#0A0A0A",
      strokeOpacity: 0,
      icons: [
        {
          icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 3 },
          offset: "0",
          repeat: "12px",
        },
      ],
    });
    line.setMap(map);
    return () => line.setMap(null);
  }, [map, pickup, dropoff]);

  return null;
};

/**
 * Fits the map to the markers ONCE on first meaningful payload, so the admin/driver/
 * passenger can pan and zoom without the periodic poll snapping the view back.
 * Re-fit is allowed only when the marker *set* changes meaningfully (different ride),
 * not when an existing driver's coordinates jitter on every location update.
 */
const MapAutoFit = ({ markers, disabled = false }) => {
  const map = useMap();
  const [hasFit, setHasFit] = useState(false);
  const prevSignatureRef = useRef("");

  useEffect(() => {
    if (disabled) return;
    if (!map || !window.google) return;
    // A signature keyed on identity (not coordinates) means a moving driver
    // doesn't keep refitting the map.
    const signature = markers
      .filter((m) => m.lat != null && m.lng != null)
      .map((m) => m.id)
      .sort()
      .join("|");
    if (!signature) return;

    const setChanged = signature !== prevSignatureRef.current;
    prevSignatureRef.current = signature;

    // Fit once on mount; afterwards only when the marker set itself changes
    // (e.g. a new ride is added or the user switches tabs).
    if (hasFit && !setChanged) return;

    const valid = markers.filter((m) => m.lat != null && m.lng != null);
    if (valid.length < 2) return;
    const bounds = new window.google.maps.LatLngBounds();
    valid.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
    map.fitBounds(bounds, 80);
    setHasFit(true);
  }, [map, markers, hasFit]);
  return null;
};

/**
 * Imperatively pans (and optionally zooms) the map to a target point when the
 * target `nonce` changes — used by the SOS auto-focus / follow overlay. It only
 * acts on a nonce change, so it never fights the dispatcher's own panning.
 */
const FocusTarget = ({ target }) => {
  const map = useMap();
  const lastNonce = useRef(null);
  useEffect(() => {
    if (!map || !target || target.nonce == null) return;
    if (target.lat == null || target.lng == null) return;
    if (target.nonce === lastNonce.current) return;
    lastNonce.current = target.nonce;
    try {
      map.panTo({ lat: target.lat, lng: target.lng });
      if (target.zoom != null) map.setZoom(target.zoom);
    } catch { /* ignore */ }
  }, [map, target]);
  return null;
};

/** Notifies the parent when the dispatcher manually drags the map, so an
 * active follow mode can yield focus instead of snapping the view back. */
const UserPanWatch = ({ onUserPan }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google) return undefined;
    let l;
    try { l = map.addListener("dragstart", () => onUserPan?.()); } catch { /* ignore */ }
    return () => { try { l?.remove?.(); } catch { /* ignore */ } };
  }, [map, onUserPan]);
  return null;
};

const FocusOnMe = ({ markers }) => {  const map = useMap();
  const doneRef = useRef(false);
  useEffect(() => {
    if (!map) return;
    const me = markers.find((m) => m.type === "me" && m.lat != null && m.lng != null);
    const others = markers.filter((m) => m.type !== "me" && m.lat != null && m.lng != null);
    if (!me || doneRef.current || others.length >= 2) return;
    doneRef.current = true;
    map.panTo({ lat: me.lat, lng: me.lng });
    map.setZoom(15);
  }, [map, markers]);
  return null;
};

/**
 * Full-screen turn-by-turn navigation camera.
 *
 * Behaves like Google Maps navigation mode:
 *   • Anchors the vehicle in the LOWER THIRD of the screen by offsetting the
 *     camera centre AHEAD of the driver along the travel heading, so the road
 *     ahead fills the upper two-thirds.
 *   • Continuously follows the live GPS position every update.
 *   • In Head-Up mode, rotates the camera bearing to the heading and applies a
 *     45° tilt for a perspective view (requires a VECTOR map; on RASTER the
 *     heading/tilt are silently ignored and the rotating marker is the fallback).
 * Camera is driven via imperative moveCamera() so it never fights controlled props.
 */
const _destPoint = (lat, lng, bearingDeg, distM) => {
  const R = 6371000;
  const br = (bearingDeg * Math.PI) / 180;
  const φ1 = (lat * Math.PI) / 180;
  const λ1 = (lng * Math.PI) / 180;
  const dr = distM / R;
  const φ2 = Math.asin(Math.sin(φ1) * Math.cos(dr) + Math.cos(φ1) * Math.sin(dr) * Math.cos(br));
  const λ2 = λ1 + Math.atan2(Math.sin(br) * Math.sin(dr) * Math.cos(φ1), Math.cos(dr) - Math.sin(φ1) * Math.sin(φ2));
  return { lat: (φ2 * 180) / Math.PI, lng: (((λ2 * 180) / Math.PI + 540) % 360) - 180 };
};

const NavCamera = ({ markers, zoom = 18, headUp = false, headingDeg = null }) => {
  const map = useMap();
  const didZoom = useRef(false);
  useEffect(() => {
    if (!map || !window.google) return;
    const me = markers.find(
      (m) => (m.type === "driver" || m.type === "me") && m.lat != null && m.lng != null,
    );
    if (!me) return;
    const hdg = Number.isFinite(headingDeg) ? (((headingDeg % 360) + 360) % 360) : 0;

    // In Head-Up (driving) mode the camera sits BEHIND the vehicle looking
    // along the route: we push the map centre AHEAD of the vehicle along the
    // travel heading so the puck rests in the lower third and the road ahead
    // fills the upper two-thirds. (Under tilt the foreground is foreshortened,
    // so we offset a generous ~30% of viewport height.) In North-Up we simply
    // centre on the vehicle so the classic map stays readable.
    let center = { lat: me.lat, lng: me.lng };
    if (headUp) {
      try {
        const div = map.getDiv();
        const h = (div && div.offsetHeight) || 800;
        const mpp = (156543.03392 * Math.cos((me.lat * Math.PI) / 180)) / Math.pow(2, zoom);
        const aheadM = h * 0.3 * mpp;
        center = _destPoint(me.lat, me.lng, hdg, aheadM);
      } catch { /* fall back to centring on the driver */ }
    }

    // Head-Up → rotate bearing to the travel direction + tilt for a 3D driving
    // perspective (VECTOR map only; ignored on RASTER). North-Up → flat, north.
    const camera = { center, heading: headUp ? hdg : 0, tilt: headUp ? 47 : 0 };
    if (!didZoom.current) { camera.zoom = zoom; didZoom.current = true; }
    try {
      if (typeof map.moveCamera === "function") map.moveCamera(camera);
      else {
        map.panTo(center);
        if (map.setHeading) map.setHeading(headUp ? hdg : 0);
        if (map.setTilt) map.setTilt(headUp ? 47 : 0);
      }
    } catch {
      try { map.panTo(center); } catch { /* ignore */ }
    }
  }, [map, markers, zoom, headUp, headingDeg]);
  return null;
};

/**
 * Reports the live Google Maps rendering type (VECTOR / RASTER / UNINITIALIZED)
 * so the deployed app can tell us whether the Map ID is vector (required for
 * Head-Up rotation). Diagnostic only.
 */
const RenderingTypeProbe = ({ onType }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || typeof map.getRenderingType !== "function") {
      onType?.("UNKNOWN");
      return undefined;
    }
    const read = () => { try { onType?.(map.getRenderingType()); } catch { onType?.("UNKNOWN"); } };
    read();
    const t = setTimeout(read, 1500);
    let l;
    try { l = map.addListener("renderingtype_changed", read); } catch { /* ignore */ }
    return () => { try { l?.remove?.(); } catch { /* ignore */ } clearTimeout(t); };
  }, [map, onType]);
  return null;
};

export default SmartMap;
